import React, { useState, useEffect } from 'react'
import ReactGA from 'react-ga4'
import Toggle from 'react-toggle'
import 'react-toggle/style.css'
import Loader from './shared/Loader'
import Captcha from './shared/Captcha'
import LegalLinks from './shared/LegalLinks'
import Modal from './shared/Modal'
import { validateEmail, showFlashMessage } from '../helpers/utils'
import { NO_IMAGE_SOURCE } from './shared/GlobalConstants'

export default function Register({
  savePath, refParams, captchaEnabled, captchaType, captchaSiteKey,
  termsUrl, privacyUrl, addendumUrl, googleMeasurementId, registerMode, registerEntity, affiliateProgramEnabled
}) {
  const [email, setEmail]                             = useState('')
  const [password, setPassword]                       = useState('')
  const [passwordValidator, setPasswordValidator]     = useState({})
  const [triggerResetCaptcha, setTriggerResetCaptcha] = useState(false)
  const [captchaToken, setCaptchaToken]               = useState(null)
  const [loading, setLoading]                         = useState(false)
  const [passwordShown, setPasswordShown]             = useState(false)
  const [invalidExtension, setInvalidExtension]       = useState(null)
  const [sameEmailTenants, setSameEmailTenants]       = useState(null)
  const initialAffiliateCode                            = refParams.affiliate_code || ''
  const hasInitialAffiliateCode                         = initialAffiliateCode.trim().length > 0
  const [affiliateCode, setAffiliateCode]              = useState(initialAffiliateCode)
  const [affiliateCodeVisible, setAffiliateCodeVisible] = useState(hasInitialAffiliateCode)
  const [completeRegistrationLoading, setCompleteRegistrationLoading] = useState(false)

  const disableSave = !(passwordValidator.length && passwordValidator.number && passwordValidator.uppercase && passwordValidator.lowercase)

  useEffect( ()=> {
    if (registerMode === 'Authentication' && googleMeasurementId) {
      ReactGA.initialize(googleMeasurementId);
    }
  },[])

  function handlePassword(event) {
    const value = event.target.value
    let validations = {}

    if (value.length >= 8) {
      validations = { ...validations, length: true }
    }
    if (value.match(/^.*\d.*$/)) {
      validations = { ...validations, number: true }
    }
    if (value.match(/^.*[A-Z].*$/)) {
      validations = { ...validations, uppercase: true }
    }
    if (value.match(/^.*[a-z].*$/)) {
      validations = { ...validations, lowercase: true }
    }

    setPasswordValidator(validations)
    setPassword(value)
  }

  function checkEmailNetworkValidity(e) {
    setInvalidExtension(null)
    const networkExtentions = ["@gmail","@icloud","@outlook","@yahoo","@hotmail","@aol","@live","@msn","@protonmail","@me","@mac","@googlemail","@facebook","@gmx","@zoho"]
    networkExtentions.map( (network)=> {
      if ( e.includes(network) ) {
        let net = network.split("@")[1];
        let res = e.split("@")[1];
        if ( res != `${net}.com` ) {
          setInvalidExtension(`${net}.com`)
        }
      }
    })
  }

  function handleSave(event) {
    event.preventDefault()

    if (email.length === 0) {
      showFlashMessage(Cas.translations.email_blank, 'error')

    } else if (!validateEmail(email)) {
      showFlashMessage(Cas.translations.email_invalid, 'error')

    } else if (disableSave) {
      showFlashMessage(Cas.translations.password_validation_alert, 'error')

    } else {
      save()
    }
  }

  function save(event) {
    setLoading(true)

    let formData = new FormData()
    formData.append('account[email]', email)
    formData.append('account[password]', password)

    if (captchaEnabled && captchaType) {
      formData.append('captcha_token', captchaToken)
    }

    appendRefParams(formData)

    Rails.ajax({
      type: 'POST',
      url: savePath,
      dataType: 'json',
      data: formData,
      success: res => {
        if(res.sameEmailTenants) {
          setSameEmailTenants(res.sameEmailTenants)
        } else if(res.redirectUrl) {
          if (registerMode === 'Authentication' && googleMeasurementId) {
            const entity = registerEntity.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\..*/, '')
            ReactGA.event({
              category: `${entity}`,
              action: "Registration",
            });
          }
          window.location.href = res.redirectUrl
        } else {
          setLoading(false)
        }
      },
      error: res => {
        showFlashMessage(res.error, 'error')
        setTriggerResetCaptcha(true)
        setLoading(false)
      }
    })
  }

  function closeDuplicateAccountModal() {
    setSameEmailTenants(null)
    setLoading(false)
    setCompleteRegistrationLoading(false)
  }

  function appendRefParams(formData) {
    for(const refParamKey in refParams) {
      if(refParamKey !== 'affiliate_code') {
        formData.append(refParamKey, refParams[refParamKey])
      }
    }

    if(affiliateProgramEnabled && affiliateCodeVisible && affiliateCode.trim().length > 0) {
      formData.append('affiliate_code', affiliateCode.trim())
    }
  }

  function completeRegistration() {
    if(completeRegistrationLoading) {
      return
    }

    setCompleteRegistrationLoading(true)

    let formData = new FormData()
    formData.append('account[email]', email)
    formData.append('account[password]', password)

    appendRefParams(formData)

    Rails.ajax({
      type: 'POST',
      url: '/complete_registration',
      dataType: 'json',
      data: formData,
      success: res => {
        if(res.redirectUrl) {
          if (registerMode === 'Authentication' && googleMeasurementId) {
            const entity = registerEntity.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\..*/, '')
            ReactGA.event({
              category: `${entity}`,
              action: "Registration",
            });
          }
          window.location.href = res.redirectUrl
        } else {
          setLoading(false)
          setCompleteRegistrationLoading(false)
        }
      },
      error: res => {
        showFlashMessage(res.error, 'error')
        setTriggerResetCaptcha(true)
        setLoading(false)
        setCompleteRegistrationLoading(false)
      }
    })
  }

  return (
    <>
      <div className='field'>
        <label className='flex-box'>
          <div>{ Cas.translations.email }</div>
          { invalidExtension && <div style={{ fontWeight: 400, color: '#cc3300', marginLeft: 5 }}>{Cas.translations.invalid_extension} {invalidExtension}?</div>}
        </label>
        <input
          type='email'
          autoFocus
          autoComplete='email'
          placeholder='some@example.com'
          value={ email }
          onChange={ event => { setEmail(event.target.value), checkEmailNetworkValidity(event.target.value) } }
        />
      </div>

      <div className='field' style={{ position: 'relative' }}>
        <label className='flex-box items-center content-space-between'>
          { Cas.translations.password }
        </label>
        <input
          type={ passwordShown ? "text" : "password" }
          autoFocus
          autoComplete='new-password'
          value={ password }
          onChange={ handlePassword }
        />
        { password.length > 0 &&
          <div className='show_password'>
            <i className={`pointer fa-light ${passwordShown ? 'fa-eye-slash' : 'fa-eye' }`} onClick={ ()=> setPasswordShown(prevState => !prevState) } />
          </div>
        }
      </div>

      { affiliateProgramEnabled &&
        <div className='field affiliate-code-field'>
          { hasInitialAffiliateCode ?
            <>
              <label className='flex-box items-center content-space-between'>
                { Cas.translations.affiliate_code }
              </label>
              <input
                type='text'
                autoComplete='off'
                value={ affiliateCode }
                readOnly
              />
            </>
            :
            <>
              <label className='affiliate-code-toggle flex-box items-center content-space-between'>
                <span>{ Cas.translations.affiliate_code_prompt }</span>
                <Toggle
                  checked={ affiliateCodeVisible }
                  icons={ false }
                  className='small'
                  onChange={ event => setAffiliateCodeVisible(event.target.checked) }
                />
              </label>

              { affiliateCodeVisible &&
                <input
                  type='text'
                  autoComplete='off'
                  value={ affiliateCode }
                  onChange={ event => setAffiliateCode(event.target.value) }
                />
              }
            </>
          }
        </div>
      }

      <div className='field flex-box flex-wrap items-center content-space-between'>
        <div className={`pass-validator ${passwordValidator.length ? '' : 'invalid'} `}>
          { Cas.translations.password_length }
        </div>
        <div className={`pass-validator ${passwordValidator.number ? '' : 'invalid'} `}>
          { Cas.translations.password_number }
        </div>
        <div className={`pass-validator ${passwordValidator.uppercase ? '' : 'invalid'} `}>
          { Cas.translations.password_uppercase }
        </div>
        <div className={`pass-validator ${passwordValidator.lowercase ? '' : 'invalid'} `}>
          { Cas.translations.password_lowercase }
        </div>
      </div>

      { captchaEnabled && captchaType &&
        <Captcha
          captchaType={ captchaType }
          captchaSiteKey={ captchaSiteKey }
          triggerResetCaptcha={ triggerResetCaptcha }
          setTriggerResetCaptcha={ setTriggerResetCaptcha }
          setCaptchaToken={ setCaptchaToken }
        />
      }

      <div className='actions'>
        <LegalLinks
          termsUrl={ termsUrl }
          privacyUrl={ privacyUrl }
          addendumUrl={ addendumUrl }
        />

        <div style={{ marginTop: 20 }}>
          { !loading ?
            <div className='flex-box items-center content-center'>
              <button type='submit' className='button' onClick={ handleSave }>
                { Cas.translations.register }
              </button>
            </div>
            :
            <div className='flex-box content-center'>
              <Loader size='small' position='centered'/>
            </div>
          }
        </div>
      </div>

      {sameEmailTenants &&
        <Modal
          visible={ true }
          mode={ 'large duplicate-account-modal' }
          closeModal={ closeDuplicateAccountModal }
          >
          <div className='duplicate-account-decision'>
            <section className='duplicate-account-hero'>
              <div className='duplicate-account-icon'>
                <i className='fa-light fa-user-check'></i>
              </div>
              <div className='duplicate-account-copy'>
                <div className='duplicate-account-eyebrow'>{ sameEmailTenants.length } { Cas.translations.choose_account_available_accounts }</div>
                <h1>{ Cas.translations.duplicate_account_found }</h1>
                <p>{ Cas.translations.duplicate_account_found_description }</p>
              </div>
            </section>

            <div className='duplicate-account-options'>
              <div className='duplicate-account-existing'>
                <div className='duplicate-account-section-title'>
                  <span>{ Cas.translations.duplicate_existing_eyebrow }</span>
                  <strong>{ Cas.translations.duplicate_existing_title }</strong>
                </div>
                <p className='duplicate-account-section-copy'>{ Cas.translations.duplicate_existing_description }</p>
                <div className='duplicate-account-grid'>
                  { sameEmailTenants.map((tenant, index) => {
                    const tenantName = tenant.application_name && tenant.application_name.length > 0 ? tenant.application_name : tenant.tenant_url

                    return (
                      <button
                        key={ index }
                        type='button'
                        className='duplicate-account-card'
                        onClick={ () => {
                          window.location.href = tenant.tenant_url
                        }}>
                        <div className='duplicate-account-logo'>
                          <img className='logotype' src={ tenant.logo ? tenant.logo : NO_IMAGE_SOURCE } alt='' />
                        </div>
                        <div className='duplicate-account-card-copy'>
                          <span>{ Cas.translations.login }</span>
                          <strong>{ tenantName }</strong>
                        </div>
                        <i className='fa-light fa-arrow-right'></i>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className='duplicate-account-new'>
                <div>
                  <span>{ Cas.translations.duplicate_new_eyebrow }</span>
                  <strong>{ Cas.translations.duplicate_new_title }</strong>
                  <p>{ Cas.translations.duplicate_new_description }</p>
                </div>
                <button
                  type='button'
                  className='button inverted duplicate-account-secondary-button'
                  disabled={ completeRegistrationLoading }
                  onClick={(e) => {
                    e.preventDefault()
                    completeRegistration()
                  }}>
                  { completeRegistrationLoading ?
                    <Loader size='small' position='centered'/>
                    :
                    <>
                      <i className='fa-light fa-plus'></i>
                      { Cas.translations.continue_creating_account }
                    </>
                  }
                </button>
              </div>
            </div>
          </div>
        </Modal>
      }
    </>
  )
}
